import { RefObject, useCallback, useEffect, useState } from 'react';

type UseLoginNudgePositionProps = {
  anchorEl: RefObject<HTMLElement> | null;
  containerRef: RefObject<HTMLElement> | null;
};
type Position = {
  top: number;
  right: number;
  width: number;
  triangle: {
    right: number;
  };
};
type UseLoginNudgePositionReturn = {
  position: Position | null;
};

export const useLoginNudgePosition = ({
  anchorEl,
  containerRef,
}: UseLoginNudgePositionProps): UseLoginNudgePositionReturn => {
  const [position, setPosition] = useState<Position | null>(null);

  const updatePosition = useCallback((): void => {
    if (!anchorEl?.current || !containerRef?.current) {
      return;
    }
    const MAX_WIDTH = 366;
    const MIN_MARGIN = 12;
    const TRIANGLE_H = 17;
    const TRIANGLE_PAD = 12;
    const ARROW_OFFSET = 12;

    const anchorRect = anchorEl.current.getBoundingClientRect();
    const navRect = containerRef.current.getBoundingClientRect();

    const availableSpace = navRect.right - MIN_MARGIN;
    const width = Math.min(MAX_WIDTH, availableSpace);

    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const distFromRight = navRect.right - anchorCenter;

    const clampedOffset = Math.max(TRIANGLE_PAD, Math.min(distFromRight, width - TRIANGLE_PAD));

    setPosition({
      width,
      top: navRect.height + TRIANGLE_H,
      right: 0,
      triangle: {
        right: clampedOffset - ARROW_OFFSET,
      },
    });
  }, [anchorEl, containerRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return (): void => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorEl, containerRef, updatePosition]);

  return { position };
};
